import { InvoiceStatus, UserRole } from '@lombok/shared';
import type { Invoice, InvoiceStatus as InvoiceStatusType, UserRole as UserRoleType } from '@lombok/shared';

/**
 * Spanish labels + groupings for the Facturas slice. Stored values stay
 * snake_case (from @lombok/shared); only what users see is Spanish. Status pill
 * colors/labels live in StatusPill (shared map) — these are the invoice-specific
 * extras: the document-kind labels and the list filter tabs.
 */

/** Invoice document kind → Spanish label. Unknown kinds fall back to the raw value. */
export const INVOICE_KIND_LABELS_ES: Record<Invoice['kind'], string> = {
  proforma: 'Proforma',
  invoice: 'Factura',
  receipt: 'Recibo',
};

/** Resolve an invoice kind to its Spanish label (raw value as fallback). */
export function invoiceKindLabel(kind: string): string {
  return INVOICE_KIND_LABELS_ES[kind as Invoice['kind']] ?? kind;
}

/** Roles that see every account's invoices and can void them (the server enforces it too). */
export const INVOICE_STAFF_ROLES: readonly UserRoleType[] = [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.FINANCE];

/** What people read as the document's name: its number (FAC-000123), or the internal id for older invoices. */
export function invoiceNumberLabel(invoice: { invoiceNumber?: string; invoiceId: string }): string {
  return invoice.invoiceNumber ?? invoice.invoiceId;
}

/** How long a just-issued (or just-voided) invoice is given to get its PDF before it counts as missing. */
export const PDF_GRACE_MS = 2 * 60 * 1000;

export type InvoicePdfState = 'ready' | 'pending' | 'missing';

/**
 * Where the invoice's PDF stands. A voided invoice's PDF only counts once it was generated AFTER
 * the void (the earlier one has no ANULADA mark). Without a current PDF it is `pending` while the
 * worker is still expected to deliver it (shortly after issuing/voiding) and `missing` after that
 * (older documents, or a failed generation): the user can then ask for it.
 */
export function invoicePdfState(
  inv: Pick<Invoice, 'status' | 'issueDate' | 'voidedAt' | 'pdfS3Key' | 'pdfGeneratedAt'>,
  now: number = Date.now(),
): InvoicePdfState {
  const voidedAfterPdf = inv.status === InvoiceStatus.VOID && Boolean(inv.voidedAt) && (inv.pdfGeneratedAt ?? '') < (inv.voidedAt ?? '');
  if (inv.pdfS3Key && !voidedAfterPdf) return 'ready';
  const since = new Date(inv.status === InvoiceStatus.VOID && inv.voidedAt ? inv.voidedAt : inv.issueDate).getTime();
  return Number.isFinite(since) && now - since < PDF_GRACE_MS ? 'pending' : 'missing';
}

/** A list filter tab: a label + the status it filters by (`undefined` = "Todas"). */
export interface InvoiceStatusTab {
  label: string;
  status?: InvoiceStatusType;
}

/**
 * Filter tabs for the Facturas list. Order puts the open/actionable states
 * (emitida, parcial, vencida) first, then the resolved ones. Drives the
 * admin/finance default tab and the customer/distributor view alike.
 */
export const INVOICE_STATUS_TABS: readonly InvoiceStatusTab[] = [
  { label: 'Emitidas', status: InvoiceStatus.ISSUED },
  { label: 'Pago parcial', status: InvoiceStatus.PARTIALLY_PAID },
  { label: 'Vencidas', status: InvoiceStatus.OVERDUE },
  { label: 'Pagadas', status: InvoiceStatus.PAID },
  { label: 'Anuladas', status: InvoiceStatus.VOID },
];

/** The tabs the Facturas list shows: "Todas" first, then one per status. */
export const INVOICE_TABS: readonly InvoiceStatusTab[] = [
  { label: 'Todas', status: undefined },
  ...INVOICE_STATUS_TABS,
];

/**
 * Statuses considered "open" (still owe money): a payment can be allocated to
 * these. Used by the allocation panel to fetch a customer's outstanding invoices.
 */
export const OPEN_INVOICE_STATUSES: readonly InvoiceStatusType[] = [
  InvoiceStatus.ISSUED,
  InvoiceStatus.PARTIALLY_PAID,
  InvoiceStatus.OVERDUE,
];
